// 미리보기 「보고서 연결」 — admin 확장 컨텍스트 타입 + 모사 배타 strip(순수 변환).
//
// 결정 기록: docs/decisions/2026-09-04-composer-preview-linked-report-and-preset-default.md
//
// ## 왜 shared가 아니라 admin 로컬 확장인가
//
// `linkedReportId`를 소비하는 것은 admin Studio 진입점(`preview-by-context`/`prefill-by-context`)
// 뿐이다 — 작성자(web) 경로·상세 live resolve·공유/PDF는 이 축을 영영 보내지 않는다. shared의
// `ReportComposerPreviewContext`에 넣으면 그 모든 경로의 타입에 "보낼 수 있는 것처럼 보이는"
// 필드가 생기므로, 소비 범위와 타입 노출 범위를 일치시켜 admin에서만 확장한다(서버는 admin
// 컨트롤러 DTO가 같은 필드를 선언해 받는다 — 그 계약이 정본).
//
// ## 배타 — 보고서 연결 중이면 **값 모사만** 싣지 않는다 (결정 §3, 2026-09-04 개정)
//
// 연결된 보고서의 저장 문서 값과 값 모사 주입이 겹치면 서버 코드 순서상 모사가 이겨, "실제
// 보고서를 보고 있다"는 화면의 말이 거짓이 된다. 서버에도 무시 가드가 있지만(방어선) 클라도
// 안 보낸다 — 가드가 클라 신뢰에만 의존하지 않게 양쪽이 같은 규칙을 진다.
// **분할 그룹(plan 축)은 예외로 보존한다**(§3 개정): 본문 행은 연결 중에도 컨텍스트 점검에서
// resolve되므로 행을 자르는 축은 직교다 — 벗기면 분할 생성된 보고서를 연결해 볼 때 통짜 행이
// 나와 실물과 어긋난다. 서버 가드도 같은 예외(restrictListRows만 통과)를 진다.

import {
  PREVIEW_SIMULATION_APPROVAL_SIGNED_AXIS_KEY,
  PREVIEW_SIMULATION_ROW_COUNT_AXIS_PREFIX,
  PREVIEW_SIMULATION_SPLIT_AXIS_KEY,
  type CatalogIndex,
  type ReportComposerPreviewContext,
} from '@safetyhub/shared';
import type { BindingProvenanceContext } from '@safetyhub/report-composer';

/**
 * 에디터 미리보기 컨텍스트 — 공용 형상 + 「보고서 연결」 축.
 *
 * `linkedReportId`: 지정 시 그 보고서의 저장 데이터로 미리보기를 렌더한다(그 점검에 실제로
 * 연결된 보고서인지는 서버가 검증). 미지정 = 현행 컨텍스트 미리보기(엔티티 기반 resolve).
 * 점검(회차) 선택 **하위** 축이라, 상위(조직·현장·점검)가 바뀌면 함께 해제된다.
 */
export interface EditorPreviewContext extends ReportComposerPreviewContext {
  linkedReportId?: string;
}

/**
 * API 전송 직전 배타 적용 — 보고서 연결 중이면 `previewSimulation`에서 **값 모사 키만** 벗긴다
 * (§3 개정: 분할 예약 키 `PREVIEW_SIMULATION_SPLIT_AXIS_KEY`와 레거시 `splitPreview`는 분할
 * 채널이라 보존 — 서버 가드의 restrictListRows 통과 예외와 같은 규칙). 값 모사 키를 벗겨 맵이
 * 비면 필드 자체를 뺀다(빈 맵 전송으로 payload를 늘리지 않음). 컨텍스트 state 자체는 건드리지
 * 않는다(연결 해제 시 모사가 그대로 복귀해야 하므로 상태에서 지우면 안 된다 — 벗기는 것은
 * 전송 형상뿐).
 */
export function toApiPreviewContext(ctx: EditorPreviewContext): EditorPreviewContext {
  if (!ctx.linkedReportId) return ctx;
  const next: EditorPreviewContext = { ...ctx };
  const simulation = next.previewSimulation;
  if (simulation) {
    const split = simulation[PREVIEW_SIMULATION_SPLIT_AXIS_KEY];
    if (split === undefined) {
      delete next.previewSimulation;
    } else {
      next.previewSimulation = { [PREVIEW_SIMULATION_SPLIT_AXIS_KEY]: split };
    }
  }
  return next;
}

/**
 * 값 출처(provenance) 재료 — **직렬화 가능한 형상**(팝아웃 창 BroadcastChannel payload에 그대로 실린다).
 * rc가 쓰는 `BindingProvenanceContext`는 Set이라 채널을 못 건너므로, 여기서는 배열로 들고
 * 수신 측이 `toBindingProvenance`로 Set을 만든다.
 */
export interface PreviewProvenanceMaterial {
  linkedReport: boolean;
  /** 값 모사 축 키(`${catalogKey}.${fieldPath}`) — **실제 전송 형상**에서 파생. */
  simulatedAxisKeys: string[];
  /** `reportScoped === true`인 카탈로그 키 — 연결 보고서 저장값 판정용. */
  reportScopedKeys: string[];
}

/**
 * 미리보기 컨텍스트 + 카탈로그 → 값 출처 표시 재료.
 *
 * ⚠️ **배타는 규칙을 베껴 적어 미러링하지 않는다 — `toApiPreviewContext`가 실제로 내보내는
 * 형상에서 파생한다.** 그래야 배타 규칙이 바뀌어도(§3 개정처럼 분할 채널이 예외로 남는 식)
 * 화면 표시가 저절로 따라간다. 규칙을 복제하면 반드시 한쪽만 낡는다.
 *
 * 전송 형상에서 **값 모사가 아닌 축**은 걸러 낸다 — 행 수 축(`__rowCount.*`)은 행 배열을
 * 확장하고, 분할 축은 행을 제한하며, 결재 서명 단계 축은 결재란을 합성한다. 셋 다 "이 칸의 값이
 * 모사다"라는 말이 아니라서 밑줄 대상이 아니다(서버 `preview-simulation.util.ts`가 같은 셋을
 * 스칼라 주입에서 건너뛴다 — 같은 경계다).
 */
export function buildPreviewProvenanceMaterial(
  ctx: EditorPreviewContext,
  catalog: CatalogIndex | undefined,
): PreviewProvenanceMaterial {
  const sent = toApiPreviewContext(ctx);
  const simulatedAxisKeys: string[] = [];
  for (const axisKey of Object.keys(sent.previewSimulation ?? {})) {
    if (axisKey === PREVIEW_SIMULATION_SPLIT_AXIS_KEY) continue;
    if (axisKey === PREVIEW_SIMULATION_APPROVAL_SIGNED_AXIS_KEY) continue;
    if (axisKey.startsWith(PREVIEW_SIMULATION_ROW_COUNT_AXIS_PREFIX)) continue;
    // `catalogKey.fieldPath` 문법만 — 서버 주입 게이트와 같은 판정(첫 점에서 가른다).
    const dot = axisKey.indexOf('.');
    if (dot <= 0 || dot === axisKey.length - 1) continue;
    simulatedAxisKeys.push(axisKey);
  }
  const reportScopedKeys = Object.entries(catalog ?? {})
    .filter(([, entry]) => entry.reportScoped === true)
    .map(([key]) => key);
  return {
    linkedReport: Boolean(ctx.linkedReportId),
    simulatedAxisKeys,
    reportScopedKeys,
  };
}

/** 재료(직렬화 형상) → rc 렌더 컨텍스트 형상(Set). */
export function toBindingProvenance(
  material: PreviewProvenanceMaterial,
): BindingProvenanceContext {
  return {
    linkedReport: material.linkedReport,
    simulatedAxisKeys: new Set(material.simulatedAxisKeys),
    reportScopedKeys: new Set(material.reportScopedKeys),
  };
}

/**
 * 서버 전송 직전 분리 — `linkedReportId`는 **body 최상위 축**이다(서버 `PreviewByContextDto`가
 * context 객체 **밖**에서 받는다 — rsel-server 교차 계약, 2026-09-04). context 안에 남긴 채
 * 보내면 sub-DTO의 `forbidNonWhitelisted`가 400을 내므로, 이 축을 받지 않는 채널
 * (prefill·split-plan)로 보낼 때도 **반드시 이 분리를 거쳐 context만** 실어야 한다.
 */
export function splitLinkedReportId(ctx: EditorPreviewContext): {
  context: ReportComposerPreviewContext;
  linkedReportId: string | undefined;
} {
  if (ctx.linkedReportId === undefined) return { context: ctx, linkedReportId: undefined };
  const { linkedReportId, ...context } = ctx;
  return { context, linkedReportId };
}

/** 점검에 연결된 보고서 목록 항목 — 서버 `preview-context-reports` 응답 형상(최신순). */
export interface PreviewContextReportItem {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  sequenceNumber: number | null;
}

/**
 * 보고서 선택지 라벨 — 제목+차수+일자 조합(점검 라벨 `N차 · … · YYYY-MM-DD` 관례를 따른다).
 * 콤보 우측 key(회색 보조)는 호출부가 status로 채운다.
 */
export function previewContextReportLabel(item: PreviewContextReportItem): string {
  // 서버 계약상 sequenceNumber는 null일 수 있다(차수 미보유 보고서 — dev 실측에서 "null차"로
  // 노출돼 적발). null이면 차수 토막을 통째로 뺀다(제목 합성의 {seq} 단어째 제거와 같은 관례).
  const seq = item.sequenceNumber === null ? '' : ` · ${item.sequenceNumber}차`;
  return `${item.title}${seq} · ${item.createdAt.slice(0, 10)}`;
}
